/**
 * useBenchmarkQueries — fires parallel performance API calls for each
 * benchmark source and returns tagged results.
 *
 * Each benchmark source defines its own scope (portfolio group, branch,
 * MAJCOM, etc.). The hook builds API params from baseParams plus the
 * scope-specific filter, fires all in parallel, and returns results
 * tagged with the source metadata.
 */
import { useState, useEffect, useRef, useMemo } from 'react'
import { api } from '../../../services/api'
import type { BenchmarkSource } from '../../../types/exec'
import type { PerfRow } from './types'

export interface BenchmarkDataSeries {
  source: BenchmarkSource
  data: PerfRow[]
  loading: boolean
  error: string | null
}

export interface UseBenchmarkQueriesResult {
  series: BenchmarkDataSeries[]
  allLoading: boolean
  allDone: boolean
}

/**
 * Build scope-specific API params for a benchmark source.
 * These are added on top of baseParams.
 */
function buildScopeParams(source: BenchmarkSource): Record<string, string> {
  switch (source.type) {
    case 'portfolio_group':
      return { portfolio_group: source.entity_code! }
    case 'sub_agency':
      return { sub_agency_code: source.entity_code! }
    case 'macom':
      return { macom_code: source.entity_code! }
    case 'subcom1':
      return { subcom1_code: source.entity_code! }
    case 'office':
      return { office_code: source.entity_code! }
    case 'all_dod':
      return {} // no scope filter = all data
    default:
      return {}
  }
}

export function useBenchmarkQueries(
  benchmarks: BenchmarkSource[],
  baseParams: Record<string, string | number>,
): UseBenchmarkQueriesResult {
  const [seriesMap, setSeriesMap] = useState<Map<string, BenchmarkDataSeries>>(new Map())
  const generation = useRef(0)

  // Stable key for dependency tracking
  const paramsKey = useMemo(
    () => JSON.stringify({ benchmarks, baseParams }),
    [benchmarks, baseParams],
  )

  useEffect(() => {
    if (benchmarks.length === 0) {
      setSeriesMap(new Map())
      return
    }

    const gen = ++generation.current

    // Initialize all as loading
    const initial = new Map<string, BenchmarkDataSeries>()
    for (const bm of benchmarks) {
      initial.set(bm.id, { source: bm, data: [], loading: true, error: null })
    }
    setSeriesMap(initial)

    // Fire all queries in parallel
    const promises = benchmarks.map(async (bm) => {
      const params: Record<string, string | number> = {
        ...baseParams,
        ...buildScopeParams(bm),
      }

      try {
        const resp = await api.getRaw<{ results: PerfRow[] }>(
          '/api/govdata/performance/',
          params,
        )
        return { id: bm.id, data: resp.results, error: null }
      } catch (err: any) {
        return { id: bm.id, data: [] as PerfRow[], error: err.message || 'Failed to load' }
      }
    })

    Promise.allSettled(promises).then((results) => {
      if (gen !== generation.current) return // stale

      setSeriesMap((prev) => {
        const next = new Map(prev)
        for (const result of results) {
          if (result.status === 'fulfilled') {
            const { id, data, error } = result.value
            const existing = next.get(id)
            if (existing) {
              next.set(id, { ...existing, data, loading: false, error })
            }
          } else {
            // Promise.allSettled won't reject, but handle defensively
          }
        }
        return next
      })
    })
  }, [paramsKey])

  const series = useMemo(() => {
    // Return in same order as benchmarks
    return benchmarks
      .map((bm) => seriesMap.get(bm.id))
      .filter((s): s is BenchmarkDataSeries => !!s)
  }, [benchmarks, seriesMap])

  const allLoading = series.some((s) => s.loading)
  const allDone = series.length > 0 && series.every((s) => !s.loading)

  return { series, allLoading, allDone }
}
